import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Store, Pizza, Truck } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import SupplyShop from "@/pages/dashboard/SupplyShop";
import PizzeriaMenu from "@/pages/dashboard/PizzeriaMenu";

const FLOW = ["recue", "en_preparation", "en_livraison", "livree"];
const LABELS = { recue: "Reçue", en_preparation: "En préparation", en_livraison: "En livraison", livree: "Livrée" };

const SECTIONS = [
  { key: "orders", label: "Commandes reçues", icon: Package },
  { key: "profile", label: "Ma pizzeria", icon: Store },
  { key: "menu", label: "Ma carte & prix", icon: Pizza },
  { key: "supply", label: "Commander boîtes & PLV", icon: Truck },
];

export default function PizzeriaDashboard({ user }) {
  const [tab, setTab] = useState("orders");
  const [piz, setPiz] = useState(null);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState({ name: "", city: "", address: "", phone: "", description: "", image_url: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/pizzerias/mine").then((r) => { setPiz(r.data); if (r.data) setForm(r.data); });
    api.get("/orders/pizzeria").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/pizzerias/mine", form); setPiz(data); toast.success("Profil enregistré"); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  const advance = async (o) => {
    const idx = FLOW.indexOf(o.status);
    const next = FLOW[Math.min(idx + 1, FLOW.length - 1)];
    try { await api.put(`/orders/${o.id}/status`, { status: next }); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <DashboardLayout title="Pizzeria Partenaire" subtitle={`Bonjour ${user?.name || ""}`} sections={SECTIONS} active={tab} onSelect={setTab}>
      <h2 className="font-display text-2xl cc-navy mb-4">{SECTIONS.find((s) => s.key === tab)?.label}</h2>

      {tab === "orders" && (
        <div>
          {!piz && <div className="card-cocarde p-6 text-slate-500 mb-3">Complétez d'abord le profil de votre pizzeria dans « Ma pizzeria ».</div>}
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} data-testid={`piz-order-${o.id}`} className="card-cocarde p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-bold cc-navy">{o.customer_name} · {o.quantity} pizza{o.quantity > 1 ? "s" : ""}</p>
                  <ul className="text-sm cc-navy mt-1" data-testid={`piz-order-items-${o.id}`}>
                    {o.items?.map((i, k) => <li key={k}>{i.quantity}× {i.pizza_name} <span className="text-slate-400">· boîte {i.box_name}</span>{i.ingredients && <span className="block text-xs text-slate-400 italic">{i.ingredients}</span>}</li>)}
                  </ul>
                  <p className="text-slate-500 text-sm">{o.delivery_address} · {o.customer_phone}</p>
                  {o.notes && <p className="text-slate-400 text-xs italic mt-1">Note : {o.notes}</p>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold rounded-full px-3 py-1 bg-slate-100 cc-navy">{LABELS[o.status] || o.status}</span>
                  {o.status !== "livree" && (
                    <button data-testid={`piz-advance-${o.id}`} onClick={() => advance(o)} className="btn-cocarde text-sm py-2 px-4">
                      Passer à « {LABELS[FLOW[FLOW.indexOf(o.status) + 1]]} »
                    </button>
                  )}
                </div>
              </div>
            ))}
            {piz && orders.length === 0 && <p className="text-slate-400">Aucune commande payée pour le moment.</p>}
          </div>
        </div>
      )}

      {tab === "profile" && (
        <form onSubmit={save} className="card-cocarde p-6 max-w-xl grid gap-3">
          <input data-testid="piz-name" required placeholder="Nom de la pizzeria" value={form.name} onChange={set("name")} className="border border-slate-300 rounded-xl px-4 py-3 outline-none focus:border-cc-red" />
          <input data-testid="piz-city" required placeholder="Ville" value={form.city} onChange={set("city")} className="border border-slate-300 rounded-xl px-4 py-3 outline-none focus:border-cc-red" />
          <input data-testid="piz-address" required placeholder="Adresse complète" value={form.address} onChange={set("address")} className="border border-slate-300 rounded-xl px-4 py-3 outline-none focus:border-cc-red" />
          <input data-testid="piz-phone" placeholder="Téléphone" value={form.phone} onChange={set("phone")} className="border border-slate-300 rounded-xl px-4 py-3 outline-none focus:border-cc-red" />
          <input data-testid="piz-image" placeholder="URL photo (facultatif)" value={form.image_url} onChange={set("image_url")} className="border border-slate-300 rounded-xl px-4 py-3 outline-none focus:border-cc-red" />
          <textarea data-testid="piz-desc" placeholder="Présentation de votre pizzeria" value={form.description} onChange={set("description")} rows={3} className="border border-slate-300 rounded-xl px-4 py-3 outline-none focus:border-cc-red" />
          <textarea data-testid="piz-managers" placeholder="Présentation des gérants (ex: Tenue par Mario & Giulia depuis 2009…)" value={form.managers || ""} onChange={set("managers")} rows={2} className="border border-slate-300 rounded-xl px-4 py-3 outline-none focus:border-cc-red" />
          <button data-testid="piz-save" disabled={busy} className="btn-cocarde disabled:opacity-60">{busy ? "Enregistrement…" : "Enregistrer mon profil"}</button>
        </form>
      )}

      {tab === "menu" && <PizzeriaMenu />}
      {tab === "supply" && <SupplyShop />}
    </DashboardLayout>
  );
}
